import * as fromReducer from './service-appointment.reducer';
import { DateTime } from 'luxon';
import * as util from '../../shared/services/util.service';
import { IAvailabilityOptions, IServiceAppointment, IServiceRequest, IVehicle, IAsyncData, IAvailability, IDealerInfo } from '@signal/asp-data-commons';
import { IServiceAppointmentForm } from './service-appointment.reducer';
import { CustomerType } from '../../shared/enums/customer-types.enum';
/* Projector function */
let totalServiceCost: number = 0;
let totalLaborHoursOfService: string | number = "";

export function vehicleFormToVehicleProjector(vehicleForm: fromReducer.IVehicleForm): IVehicle {
  const vehicleInfo: IVehicle = {} as IVehicle;

  if (vehicleForm.isOwnVehicle) {
    vehicleInfo.isOwnVehicle = true;
    vehicleInfo.imageUrl = vehicleForm.imageUrl;
    vehicleInfo.description = vehicleForm.description;
  } else {
    vehicleInfo.isOwnVehicle = false;
    if(vehicleForm.make && vehicleForm.model){
      vehicleInfo.description = `${vehicleForm.make} ${vehicleForm.model}`; 
    }else if(vehicleForm.description) {
      vehicleInfo.description = vehicleForm.description;
    }else {
      vehicleInfo.description = '';
    }
    vehicleInfo.imageUrl = vehicleForm.imageUrl;
  }

  vehicleInfo.vin = vehicleForm.vin;
  vehicleInfo.make = vehicleForm.make?.toLowerCase() === 'other' ? vehicleForm.otherMake : (vehicleForm.make ? vehicleForm.make?.charAt(0)?.toUpperCase() + vehicleForm.make?.substr(1)?.toLowerCase() : vehicleForm.make);
  vehicleInfo.year = vehicleForm.year?.toString() || null;
  vehicleInfo.model = vehicleForm.model;
  vehicleInfo.mileage = +vehicleForm.mileage;
  vehicleInfo.ppmEligible = vehicleForm.ppmEligible;
  vehicleInfo.protectionProducts = vehicleForm.protectionProducts;
  return vehicleInfo;
}

export const appointmentFormToDetailMap =
  (data: { appointmentForm: fromReducer.IServiceAppointmentForm; availabilityOptions: IAvailabilityOptions },
    dealerDetails: IDealerInfo) => {
    const appointmentForm: fromReducer.IServiceAppointmentForm = data.appointmentForm;
    const isRemoteLocation = appointmentForm.customerForm.transport === 'REMOTE' || appointmentForm.customerForm.transport === 'PICKUP' || appointmentForm.customerForm.transport === 'DELIVERY';
    const availabilityOptions = data.availabilityOptions;
    if (appointmentForm) {
      const date = `${DateTime.fromISO(appointmentForm.availabilityForm.availability.appointmentStartDate).toFormat('MMMM dd')}`;
      const time = appointmentForm.availabilityForm.availability.timeSlotId;

      const dateTime = `${date} at ${time}`;
      const vehicle = getSelectedVehicleSummary(appointmentForm.vehicleForm);
      const serviceFormData = buildSelectedServicesInfo(appointmentForm.serviceForm);

      let services = '';
      let cost = 0;
      if (serviceFormData && serviceFormData.length > 0) {
        serviceFormData.map(s => s?.details).forEach(t => {
          const serviceRequestPrice = +t?.pricing?.final;
          const coveredByToyotaCare = t?.recommended?.isCoveredByToyotaCare;
          const displayName = t?.displayName ? t.displayName : t?.dmsDescription;
          const lexusComplimentaryText = 'service.lexusComplimentaryMaintenance';
          const toyotaComplimentaryText = 'service.toyotaComplimentaryMaintenance';
          const coveredByToyotaCareLabelText = 'service.coveredByToyotaCare';
          const coveredByToyotaCareText = dealerDetails?.dealerCode?.startsWith('6')
            ? lexusComplimentaryText
            : (dealerDetails?.address?.country === 'MEX'
              ? toyotaComplimentaryText
              : coveredByToyotaCareLabelText);
          services += `${displayName} - ${coveredByToyotaCare ?
            coveredByToyotaCareText :
            util.getOpcodeDisplayPricing(t)}\n`;
          cost = cost + (!isNaN(serviceRequestPrice) && !coveredByToyotaCare ? serviceRequestPrice : 0);
        });
      }

      let customerAddress = '';
      let fields = [];
      let zip = '';
      const customer = appointmentForm.customerForm;
      if (isRemoteLocation) {
        fields = [customer.pickUpAddress.addressLine1, customer.pickUpAddress.addressLine2, customer.pickUpAddress.city, customer.pickUpAddress.state];
        zip = customer.pickUpAddress.zip;
        const addressFields = fields?.filter(f => !!f)?.join(', ');
        if (appointmentForm.customerForm.transport === 'REMOTE' || appointmentForm.customerForm.transport === 'PICKUP') {
          customerAddress = `otherTexts.weWillPickYourCarAt:\n ${addressFields ? addressFields + ' - ' : ''}${zip}`;
        }


        const OtherFields = [customer.dropOffAddress.addressLine1, customer.dropOffAddress.addressLine2, customer.dropOffAddress.city, customer.dropOffAddress.state];
        const otherZip = customer.dropOffAddress.zip;
        const OtherAddressFields = OtherFields?.filter(f => !!f)?.join(', ');
        if (appointmentForm.customerForm.transport === 'REMOTE' || appointmentForm.customerForm.transport === 'DELIVERY') {
          customerAddress += `\n\notherTexts.weWillDropOffYourCarAt:\n ${OtherAddressFields ? OtherAddressFields + ' - ' : ''}${otherZip}`;
        }
        if (customer.isDropOffAddress) {
          customerAddress = `otherTexts.weWillPickAndDropOffYourCarAt:\n ${addressFields ? addressFields + ' - ' : ''}${zip}`;
        }
        if (isRemoteLocation) {
          customerAddress += '\n\notherTexts.dealershipWillContactYouOnceTheServiceIsCompleted';
        }
      }

      const dealershipAddress = `otherTexts.serviceTheCarAt: ${dealerDetails?.dealerName} \n ${dealerDetails?.appointmentAddresses?.serviceAppointmentAddress?.addressShort.length > 0 ? dealerDetails?.appointmentAddresses?.serviceAppointmentAddress?.addressShort : dealerDetails?.addressShort}`;

      const transportLocation = availabilityOptions?.transport?.transportOptions?.find(opt=> opt.id === appointmentForm.customerForm.transport);

      const advisorName = availabilityOptions?.advisors
        ?.find(advisor => advisor.id === appointmentForm.availabilityForm.availability.advisorId)
        ?.displayName;
      const detailMap = {
        'service.time': dateTime,
        'service.pickupTimes': dateTime,
        'testDrive.vehicle': vehicle,
        'serviceSchedule.advisor': advisorName,
        'testDrive.services': services,
        'service.estimatedCost': util.toUsDollar(cost),
        // 'Coupons': 'Alignment Check',
        'service.transportation': (transportLocation?.label)?`service.${transportLocation.label}`:'',
        'service.location': `${(isRemoteLocation ? customerAddress : dealershipAddress)}`
      };
      if (isRemoteLocation) {
        delete detailMap['service.time'];
      }
      if (!isRemoteLocation) {
        delete detailMap['service.pickupTimes'];
      }
      return detailMap as { [key: string]: string };
    }
  };

export const buildSelectedServicesInfo = (serviceForm: fromReducer.IServiceRequestForm): IServiceRequest[] => {
  return serviceForm.selectedServices.map(t => {
    return { ...t } as IServiceRequest;
  });
};

export const getSelectedVehicleSummary = (vehicleForm: fromReducer.IVehicleForm) => {
  const selectedVehicle = vehicleFormToVehicleProjector(vehicleForm);
  let vehicleInfo =selectedVehicle.year && selectedVehicle.model ? `${selectedVehicle.year || ''} ${selectedVehicle.model || ''}` : '';
  vehicleInfo = vehicleInfo ? `${vehicleInfo} \n` : vehicleInfo;
  return selectedVehicle.vin ? `${vehicleInfo} ${selectedVehicle.vin}` : vehicleInfo;
};

export const transportToIsRemoteLocationProjector = (appointmentForm: fromReducer.IServiceAppointmentForm) => {
  return (appointmentForm.customerForm.transport === 'DELIVERY' || appointmentForm.customerForm.transport === 'REMOTE' || appointmentForm.customerForm.transport === 'PICKUP');
};

export const vehicleToMakeModelYearTextProjector = (vehicleForm: fromReducer.IVehicleForm) => {
  return (vehicleForm.year && vehicleForm?.model) ? `${vehicleForm.year || ''} ${vehicleForm.model || ''}` : vehicleForm?.vin ? `VIN ${vehicleForm.vin}` : '';
};

export const appointmentToAppointmentFormProjector =
  (a: IServiceAppointment, isDashboardRoutePath: boolean): fromReducer.IServiceAppointmentForm => {
    const appointmentVehicle: IVehicle = a.vehicle;
    const isOwnVehicle = isDashboardRoutePath ? false : appointmentVehicle.isOwnVehicle;
    const appointmentId = (a.appointmentId) ? (a.appointmentId) : '';
    const isInputVinBased = !!(!isOwnVehicle || appointmentVehicle.vin);
    const isMakeOther = appointmentVehicle.make?.toLowerCase() !== 'toyota' && appointmentVehicle.make?.toLowerCase() !== 'lexus' && appointmentVehicle.make?.toLowerCase() !== 'scion';
    const make = isMakeOther ? 'Other' : (appointmentVehicle.make?.charAt(0)?.toUpperCase() + appointmentVehicle.make?.substr(1)?.toLowerCase());

    const vehicleForm: fromReducer.IVehicleForm = {
      isOwnVehicle,
      isInputVinBased,
      description: appointmentVehicle.description,
      imageUrl: appointmentVehicle.imageUrl,
      make: make,
      otherMake: isMakeOther && appointmentVehicle.make,
      year: appointmentVehicle.year,
      model: appointmentVehicle.model,
      vin: appointmentVehicle.vin,
      mileage: appointmentVehicle.mileage,
    };

    const selectedServices: IServiceRequest[] = a.serviceRequests;

    const availabilityForm: fromReducer.IAvailabilityForm = {
      availability: {
        languageId: a.languageId,
        advisorId: a.advisorId,
        timeSlotId: a.timeSlotId,
        appointmentStartDate: DateTime.fromISO(a.appointmentStartDate).toFormat('yyyy-MM-dd')
      }
    };

    const isRemoteLocation = !!(a.dropOffAddress);
    const isDropOffAddress = !!(a.customer.isDropOffAddress);
    let comments = a.appointmentComments?.replace('| Eligible for Complimentary Rental - YES', '')?.replace('| Eligible for Protection Products - YES', '') || a.appointmentComments;
    comments = comments && a.vehicle.protectionProducts ? comments.replace(` | Valid Protection Plans -  ${a.vehicle.protectionProducts}`, '') : comments;
    let customerForm: fromReducer.ICustomerForm = {
      transport: a.transportationTypeCode,
      remoteZipCode: a.pickUpAddress?.zip,
      firstName: a.customer.firstName,
      lastName: a.customer.lastName,
      phoneNumber: a.customer.phoneNumber,
      email: a.customer.emailAddress,
      customerType: a.customer.customerType === CustomerType.BUSINESS ? CustomerType.BUSINESS : CustomerType.INDIVIDUAL,
      businessName: a.customer.businessName,
      isRemoteLocation,
      pickUpAddress: {
        addressLine1: isRemoteLocation ? a.pickUpAddress.addressLine1 : '',
        addressLine2: isRemoteLocation ? a.pickUpAddress.addressLine2 : '',
        city: (isRemoteLocation) ? a.pickUpAddress.city : '',
        state: (isRemoteLocation) ? a.pickUpAddress.state : '',
        zip: (isRemoteLocation) ? a.pickUpAddress.zip : '',
      },
      dropOffAddress: {
        addressLine1: (isRemoteLocation) ? a.dropOffAddress.addressLine1 : '',
        addressLine2: (isRemoteLocation) ? a.dropOffAddress.addressLine2 : '',
        city: (isRemoteLocation) ? a.dropOffAddress.city : '',
        state: (isRemoteLocation) ? a.dropOffAddress.state : '',
        zip: (isRemoteLocation) ? a.dropOffAddress.zip : '',
      },
      isDropOffAddress,
      appointmentComments: comments
    };
    if (a.customer.address && a.customer.address.addressLine1) {
      customerForm = {
        ...customerForm,
        customerAddress: {
          addressLine1: a.customer.address.addressLine1,
          addressLine2: a.customer.address.addressLine2,
          city: a.customer.address.city,
          state: a.customer.address.state,
          zip: a.customer.address.zip
        },
      }
    };
    return {
      appointmentStatus: a.appointmentStatus,
      vehicleForm,
      recallCodes: [],
      serviceForm: {
        selectedServices,
        isInitialState: false
      },
      availabilityForm,
      customerForm,
      appointmentId
    };
  };

export function appointmentVehicleFormToSummaryProjector(vehicleForm: fromReducer.IVehicleForm) {
  /* Vehicle summary */
  const chosenVehicle = (vehicleForm.year && vehicleForm.model) ? `${vehicleForm.year} ${vehicleForm.model}` : '';
  return [{
    key: 'testDrive.vehicle',
    value: chosenVehicle
  }];
}

export function appointmentServiceRequestFormToSummaryProjector(serviceForm: fromReducer.IServiceRequestForm, transportation: boolean, availability: IAsyncData<IAvailability>,
  appointmentForm: IServiceAppointmentForm, showPricing: boolean = true) {
  /* Services summary */
  const selectedServices = serviceForm.selectedServices;
  let selectedServicesCost = 0;
  let selectedServicesHours = 0;
  if (selectedServices && selectedServices.length > 0) {
    selectedServices.map(s => s?.details).forEach(t => {
      const coveredByToyotaCare = t?.recommended?.isCoveredByToyotaCare;
      const serviceRequestPrice = t?.pricing?.final;
      selectedServicesCost += (!isNaN(+serviceRequestPrice) && !coveredByToyotaCare ? +serviceRequestPrice : 0);
      selectedServicesHours += t?.labourHours;
    });
  }
  const totalCost = selectedServices.length && !isNaN(selectedServicesCost) ? util.toUsDollar(selectedServicesCost) : '';
  totalServiceCost = selectedServices.length && !isNaN(selectedServicesCost) ? selectedServicesCost : 0;
  const defaultDisclaimerText = 'otherTexts.thisIsAnEstimatedPriceActualPriceMayVaryBasedOnTheVehicleTypeAndSize';
  const servicePriceSummary = {
    key: 'service.estimatedPrice',
    value: totalCost,
    disclaimer: defaultDisclaimerText
  };
  const NORemote = `${selectedServices.length && selectedServicesHours ? selectedServicesHours.toFixed(2) + ' hrs' : ''}`
  let Remote = 'NA';
  if (transportation) {
    for (let i = 0; i < availability.data.availableDates?.length; i++) {
      if (availability.data.availableDates[i]?.date == appointmentForm.availabilityForm.availability.appointmentStartDate) {
        for (let j = 0; j < availability.data.availableDates[i]?.timeSlots.length; j++) {
          if (availability.data.availableDates[i]?.timeSlots[j].timeSlotId == appointmentForm.availabilityForm.availability.timeSlotId) {
            if (availability.data.availableDates[i]?.timeSlots[j].remoteEstimatedCompletionTime) {
              Remote = `${DateTime.fromISO(availability.data.availableDates[i]?.timeSlots[j].remoteEstimatedCompletionTime, { zone: 'America/New_York' }).toFormat("LLL dd' at 'hh:mm a")}`;
            }
          }
        }
      }
    }
  }
  totalLaborHoursOfService = transportation ? DateTime.fromFormat(Remote, "LLL dd' at 'hh:mm a").toFormat("hh:mm") : selectedServicesHours;
  const serviceHoursSummary = {
    key: transportation ? 'otherTexts.estimatedTimeForServiceCompletion' : 'otherTexts.estimatedHours',
    value: transportation ? Remote : NORemote,
    disclaimer: 'otherTexts.thisIsAnEstimatedHoursActualHoursMayVaryBasedOnTheVehicleTypeAndSize'
  };

  return showPricing ? [servicePriceSummary, serviceHoursSummary] : [serviceHoursSummary];
}

export const getSelectedServices = (services: any[]) => {
  return (services)
    .filter(t => t.selected)
    .map(t => {
      let subComplaints=[];
      if(t.serviceInfo?.isInternalCode && t?.addOns && t?.addOns.length)
      {
        subComplaints=t?.addOns.filter(c=>c.selected===true).map(t=>t.serviceInfo);
      } 
      const internalCode= t.serviceInfo?.isInternalCode ? t.serviceInfo?.opcode :"";
      return { opcode: t.serviceInfo.opcode, comments: t.comments, details: t.serviceInfo , internalCode, subComplaints };
    });
};
export const getTotalCostAndLabourHrs = () => {
  const cost = isNaN(parseInt(totalServiceCost.toString())) ? 0 : parseInt(totalServiceCost.toString());
  const hrs = isNaN(parseInt(totalLaborHoursOfService.toString())) ? 0 : parseInt(totalLaborHoursOfService.toString());
  return { cost, hrs };
}
export const editAppointmentDetails = (appt: IServiceAppointment): IServiceAppointment => {
  if (appt && appt.vehicle && appt.vehicle.make?.toLowerCase() !== 'toyota' && appt.vehicle.make?.toLowerCase() !== 'lexus' && appt.vehicle.make?.toLowerCase() !== 'scion') {
    const vehicle = { ...appt.vehicle, make: 'other' }
    appt = { ...appt, vehicle }
  }
  return appt;
}
